import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    DefaultButton,
    MessageBar,
    MessageBarType,
    mergeStyleSets,
    FontIcon
} from '@fluentui/react';
import SyncButton from './SyncButton';

// Phase 4: card-based Dashboard. Replaces the interim per-direction
// DetailsList tables with one card per account showing tenantName, the
// authenticated user, calendar counts (upstream / downstream / both), and
// a mini-list of that account's calendars. The Sync Overview card at the
// top and the Sync History DetailsList at the bottom keep all existing
// information visible — nothing was removed, only re-laid out.
const styles = mergeStyleSets({
    card: {
        background: '#ffffff',
        border: '1px solid #edebe9',
        borderRadius: 4,
        boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.06), 0 0.3px 0.9px 0 rgba(0,0,0,0.04)',
        padding: 16
    },
    accountCard: {
        background: '#ffffff',
        border: '1px solid #edebe9',
        borderRadius: 4,
        boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.06), 0 0.3px 0.9px 0 rgba(0,0,0,0.04)',
        padding: 16,
        width: 360,
        minWidth: 320,
        flex: '0 1 360px'
    },
    badge: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        marginRight: 6
    },
    badgeUpstream: { background: '#deecf9', color: '#005a9e' },
    badgeDownstream: { background: '#fff4ce', color: '#797673' },
    badgeBoth: { background: '#dff6dd', color: '#107c10' },
    statusOk: { color: '#107c10', fontWeight: 600 },
    statusFail: { color: '#a4262c', fontWeight: 600 },
    statusPending: { color: '#797673', fontWeight: 600 },
    muted: { color: '#605e5c' },
    mono: { fontFamily: 'Consolas, monospace', color: '#605e5c' }
});

function Dashboard() {
    const [clientPrincipal, setClientPrincipal] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [calendars, setCalendars] = useState({ upstream: [], downstream: [] });
    const [syncLogs, setSyncLogs] = useState([]);

    useEffect(() => {
        fetch('/.auth/me')
            .then(response => response.json())
            .then(data => setClientPrincipal(data.clientPrincipal));
    }, []);

    useEffect(() => {
        if (clientPrincipal) {
            fetchStatus();
            fetchCalendars();
            fetchAccounts();
        }
    }, [clientPrincipal]);

    const fetchStatus = async () => {
        if (!clientPrincipal) return;
        const [statusResponse, logsResponse] = await Promise.all([
            fetch('/api/sync-status', {
                headers: { 'x-ms-client-principal-id': clientPrincipal.userId }
            }),
            fetch('/api/sync-logs', {
                headers: { 'x-ms-client-principal-id': clientPrincipal.userId }
            })
        ]);
        const statusData = await statusResponse.json();
        const logsData = await logsResponse.json();
        setSyncStatus(statusData);
        setSyncLogs(Array.isArray(logsData) ? logsData : []);
    };

    const fetchCalendars = async () => {
        const response = await fetch('/api/calendars-list', {
            headers: { 'x-ms-client-principal-id': clientPrincipal.userId }
        });
        const data = await response.json();
        setCalendars(data);
    };

    const fetchAccounts = async () => {
        const response = await fetch('/api/accounts-list', {
            headers: { 'x-ms-client-principal-id': clientPrincipal.userId }
        });
        if (!response.ok) return;
        const data = await response.json().catch(() => []);
        setAccounts(Array.isArray(data) ? data : []);
    };

    const renderStats = (log) => {
        if (!log?.stats) return `Events: ${log.eventsProcessed || 0}`;
        const { additions = 0, updates = 0, deletions = 0, ignored = 0 } = log.stats;
        return `Added: ${additions}, Updated: ${updates}, Deleted: ${deletions}, Skipped: ${ignored}`;
    };

    // Pair detection: two rows form a bidirectional "Both" calendar when
    // they share (tenantId, mailboxId) but differ on `type`. Used both for
    // the count summary and the per-calendar badge.
    const allCalendars = [...calendars.upstream, ...calendars.downstream];
    const findMate = (item) => allCalendars.find(c =>
        c.id !== item.id &&
        c.tenantId === item.tenantId &&
        c.mailboxId === item.mailboxId &&
        c.type !== item.type
    );

    const calendarsForAccount = (account) => allCalendars.filter(c => c.tenantId === account.tenantId);
    const summariseAccount = (account) => {
        const rows = calendarsForAccount(account);
        const pairKeys = new Set();
        let upstream = 0, downstream = 0, both = 0;
        for (const row of rows) {
            const mate = findMate(row);
            if (mate) {
                const key = `${row.tenantId}|${row.mailboxId}`;
                if (!pairKeys.has(key)) {
                    pairKeys.add(key);
                    both += 1;
                }
            } else if (row.type === 'upstream') {
                upstream += 1;
            } else if (row.type === 'downstream') {
                downstream += 1;
            }
        }
        return { upstream, downstream, both };
    };

    const lastSyncStatus = syncStatus?.lastSync?.status;
    const statusClass = lastSyncStatus === 'completed' ? styles.statusOk
        : lastSyncStatus === 'failed' ? styles.statusFail
        : styles.statusPending;

    const renderAccountCard = (account) => {
        const { upstream, downstream, both } = summariseAccount(account);
        const rows = calendarsForAccount(account);
        return (
            <div key={account.id} className={styles.accountCard}>
                <Stack tokens={{ childrenGap: 8 }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <FontIcon iconName="Org" />
                        <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                            {account.tenantName || account.tenantId}
                        </Text>
                    </Stack>

                    {(account.userDisplayName || account.userEmail) && (
                        <Stack>
                            <Text>{account.userDisplayName || account.userEmail}</Text>
                            {account.userDisplayName && account.userEmail && (
                                <Text variant="small" className={styles.muted}>
                                    {account.userEmail}
                                </Text>
                            )}
                        </Stack>
                    )}

                    <Text variant="small" className={styles.mono}>
                        {account.tenantId}
                    </Text>

                    <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
                        {upstream > 0 && (
                            <span className={`${styles.badge} ${styles.badgeUpstream}`}>
                                ↑ {upstream} upstream
                            </span>
                        )}
                        {downstream > 0 && (
                            <span className={`${styles.badge} ${styles.badgeDownstream}`}>
                                ↓ {downstream} downstream
                            </span>
                        )}
                        {both > 0 && (
                            <span className={`${styles.badge} ${styles.badgeBoth}`}>
                                ↕ {both} bidirectional
                            </span>
                        )}
                        {rows.length === 0 && (
                            <Text variant="small" className={styles.muted}>
                                No calendars configured.
                            </Text>
                        )}
                    </Stack>

                    {rows.length > 0 && (
                        <Stack tokens={{ childrenGap: 4 }} styles={{ root: { marginTop: 4 } }}>
                            {(() => {
                                // Collapse pair-mates into a single entry so
                                // bidirectional calendars don't appear twice.
                                const seen = new Set();
                                const items = [];
                                for (const row of rows) {
                                    const key = `${row.tenantId}|${row.mailboxId}`;
                                    if (seen.has(key)) continue;
                                    seen.add(key);
                                    const mate = findMate(row);
                                    items.push({ row, paired: !!mate });
                                }
                                return items.map(({ row, paired }) => (
                                    <Stack
                                        key={row.id}
                                        horizontal
                                        verticalAlign="center"
                                        tokens={{ childrenGap: 8 }}
                                    >
                                        <Text
                                            styles={{
                                                root: {
                                                    minWidth: 80,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: paired ? '#107c10'
                                                        : row.type === 'upstream' ? '#005a9e'
                                                        : '#797673'
                                                }
                                            }}
                                        >
                                            {paired ? '↕ both' : row.type === 'upstream' ? '↑ upstream' : '↓ downstream'}
                                        </Text>
                                        <Stack styles={{ root: { flex: 1, minWidth: 0 } }}>
                                            <Text styles={{ root: { overflow: 'hidden', textOverflow: 'ellipsis' } }}>
                                                {row.calendarName || '(unnamed calendar)'}
                                            </Text>
                                            <Text variant="small" className={styles.muted}>
                                                {row.ownerEmail
                                                    || (row.userIdInTenant && row.userIdInTenant !== account.userId
                                                        ? row.userIdInTenant : null)
                                                    || account.userEmail
                                                    || '(owner unknown)'}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                ));
                            })()}
                        </Stack>
                    )}
                </Stack>
            </div>
        );
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {/* Overview card */}
            <div className={styles.card}>
                <Stack tokens={{ childrenGap: 12 }}>
                    <Text variant="xLarge">Sync Status Overview</Text>
                    <Stack horizontal wrap tokens={{ childrenGap: 24 }}>
                        <Stack>
                            <Text variant="small" className={styles.muted}>Interval</Text>
                            <Text>{syncStatus?.settings?.refreshIntervalMinutes ?? '—'} minutes</Text>
                        </Stack>
                        <Stack>
                            <Text variant="small" className={styles.muted}>Last sync</Text>
                            <Text>
                                {syncStatus?.lastSync?.timestamp
                                    ? new Date(syncStatus.lastSync.timestamp).toLocaleString()
                                    : 'Never'}
                            </Text>
                        </Stack>
                        <Stack>
                            <Text variant="small" className={styles.muted}>Status</Text>
                            <Text className={statusClass}>
                                {lastSyncStatus || 'Not started'}
                            </Text>
                        </Stack>
                        {syncStatus?.lastSync?.stats && (
                            <Stack>
                                <Text variant="small" className={styles.muted}>Last run</Text>
                                <Text>{renderStats(syncStatus.lastSync)}</Text>
                            </Stack>
                        )}
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <DefaultButton text="Refresh Status" onClick={fetchStatus} />
                        <SyncButton
                            clientPrincipal={clientPrincipal}
                            onSyncComplete={fetchStatus}
                        />
                    </Stack>
                </Stack>
            </div>

            {accounts.length === 0 && (
                <MessageBar messageBarType={MessageBarType.info}>
                    No tenants added yet — visit Settings to add a tenant and pick which calendars to sync.
                </MessageBar>
            )}

            {accounts.length > 0
                && calendars.upstream.length === 0
                && calendars.downstream.length === 0 && (
                <MessageBar messageBarType={MessageBarType.info}>
                    Tenants added but no calendars configured yet — visit Settings to pick calendars.
                </MessageBar>
            )}

            {/* Per-account cards grid */}
            {accounts.length > 0 && (
                <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                    {accounts.map(renderAccountCard)}
                </Stack>
            )}

            {/* Sync history card */}
            {syncLogs.length > 0 && (
                <div className={styles.card}>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="large">Sync History</Text>
                        <DetailsList
                            items={syncLogs}
                            layoutMode={DetailsListLayoutMode.justified}
                            selectionMode={SelectionMode.none}
                            columns={[
                                {
                                    key: 'timestamp',
                                    name: 'Time',
                                    fieldName: 'timestamp',
                                    minWidth: 140,
                                    maxWidth: 200,
                                    isResizable: true,
                                    onRender: (item) => new Date(item.timestamp).toLocaleString()
                                },
                                {
                                    key: 'status',
                                    name: 'Status',
                                    fieldName: 'status',
                                    minWidth: 100,
                                    maxWidth: 140,
                                    isResizable: true,
                                    onRender: (item) => (
                                        <Text
                                            className={
                                                item.status === 'completed' ? styles.statusOk
                                                    : item.status === 'failed' ? styles.statusFail
                                                    : styles.statusPending
                                            }
                                        >
                                            {item.status}
                                        </Text>
                                    )
                                },
                                {
                                    key: 'stats',
                                    name: 'Statistics',
                                    minWidth: 200,
                                    isResizable: true,
                                    onRender: (item) => renderStats(item)
                                },
                                {
                                    key: 'error',
                                    name: 'Error',
                                    minWidth: 160,
                                    isResizable: true,
                                    onRender: (item) => item.errorMessage
                                        ? <Text className={styles.statusFail}>{item.errorMessage}</Text>
                                        : null
                                }
                            ]}
                        />
                    </Stack>
                </div>
            )}
        </Stack>
    );
}

export default Dashboard;
